import { useCallback, useEffect, useRef, useState } from 'react';
import useSharePdf from '../../hooks/useSharePdf';
import { fetchMyExport, fetchTimesheetExport, recordPayrollRun } from '../../utils/timesheetsApi';
import {
  crewExportFilename, crewExportPdfDoc, crewExportTitle, downloadCrewExportCsv,
  downloadTimesheetCsv, exportFilename, exportTitle, timesheetExportPdfDoc,
} from '../../utils/timesheetExport';

/**
 * RUN one timesheet export: ask the server, then save the CSV or the PDF.
 *   - manager (default): `GET /api/timesheets/export`, `utils/timesheetExport.js#downloadTimesheetCsv`
 *     / `timesheetExportPdfDoc`, and the run is RECORDED (`POST /payroll-runs`).
 *   - `{ crew: true }` (owner 2026-11-01, hours only): `GET /api/timesheets/my-export` — the
 *     caller's OWN weeks — `downloadCrewExportCsv` / `crewExportPdfDoc`, and NOTHING is recorded
 *     (export tracking is the manager's payroll run; a crew member's copy is not one).
 *
 * 🖨️ The PDF goes through THE house PDF path, `hooks/useSharePdf` (pdfmake,
 * vector text) — never a second pdfmake loader. Its `filename` is read at render,
 * so the download fires from an EFFECT after the fetched document is in state,
 * i.e. from the render that already carries the right filename and doc.
 *
 * `run(request, opts)` resolves `{ ok, error }` and never throws:
 * a refusal shows the server's own words, a network failure says so.
 */
const MANAGER = Object.freeze({
  fetch: fetchTimesheetExport,
  filename: (p) => exportFilename(p.doc, 'pdf', p.opts),
  title: (p) => exportTitle(p.doc, p.opts),
  pdfDoc: (doc, request, opts) => timesheetExportPdfDoc(doc, request.sections, exportTitle(doc, opts)),
  csv: (doc, request, opts) => downloadTimesheetCsv(doc, request.sections, opts),
  records: true,
});
const CREW = Object.freeze({
  fetch: fetchMyExport,
  filename: (p) => crewExportFilename(p.doc, 'pdf'),
  title: (p) => crewExportTitle(p.doc),
  pdfDoc: (doc) => crewExportPdfDoc(doc),
  csv: (doc) => downloadCrewExportCsv(doc),
  records: false,
});

export default function useTimesheetExport({ crew = false } = {}) {
  const kind = crew ? CREW : MANAGER;
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { doc, sections, opts } awaiting a PDF save
  const docRef = useRef(null);

  const { handleDownload } = useSharePdf({
    renderPdfMakeDoc: () => docRef.current,
    filename: pending ? kind.filename(pending) : 'timesheets.pdf',
    title: pending ? kind.title(pending) : 'Timesheets',
    onActionComplete: () => { docRef.current = null; setPending(null); setBusy(false); },
  });

  // ONE save per fetched document: `handleDownload` changes identity while it runs.
  const firedFor = useRef(null);
  useEffect(() => {
    if (!pending || !docRef.current || firedFor.current === pending) return;
    firedFor.current = pending;
    handleDownload();
  }, [pending, handleDownload]);

  const run = useCallback(async (request, opts = {}) => {
    setBusy(true);
    let r;
    try {
      r = await kind.fetch(request);
    } catch (e) {
      setBusy(false);
      return { ok: false, error: 'Could not reach the server. Check your connection and try again.' };
    }
    if (!r.ok) {
      setBusy(false);
      return { ok: false, error: (r.body && r.body.error) || 'The export did not work.' };
    }
    const doc = r.body;
    // EXPORT TRACKING (D2, 2026-10-29): THIS is the download path — the only place an export
    // counts — so the MANAGER's run is recorded (`POST /payroll-runs`, the server re-derives the
    // weeks from the same filters). A failed record never blocks the file (`recordError`).
    const record = async () => {
      if (!kind.records) return null;
      try {
        const rec = await recordPayrollRun({ ...request, format: request.format === 'pdf' ? 'pdf' : 'csv', generatedAt: doc.generated_at });
        return rec.ok ? null : ((rec.body && rec.body.error) || 'The export was not recorded.');
      } catch (e) {
        return 'The export was not recorded — check your connection.';
      }
    };
    if (request.format === 'pdf') {
      docRef.current = kind.pdfDoc(doc, request, opts);
      setPending({ doc, sections: request.sections, opts });
      return { ok: true, recordError: await record() };
    }
    kind.csv(doc, request, opts);
    setBusy(false);
    return { ok: true, recordError: await record() };
  }, [kind]);

  return { run, busy };
}
